/**
 * Slice 40 — connecting a Meta ad account, and nothing else.
 *
 * ## WHY THIS IS A SEPARATE CONTRACT FROM `integration.ts`'s META ARM
 *
 * `meta_whatsapp` and `meta_ads` reach the same Graph host with the same kind of
 * system user token, and that similarity is the trap. They are different
 * permissions over different objects with different consequences: a WhatsApp
 * token that has gone stale stops a message; an ads token that has gone stale
 * stops Wend from **pausing a campaign that is spending money**. Sharing one
 * config shape would make a single `wabaId`-and-`phoneNumberId` row the place
 * somebody later hangs an ad account off, and the two would then expire
 * together in the read even though Meta expires them apart.
 *
 * So: its own `integration_kind`, its own row, its own check. What is shared is
 * shared deliberately and named — {@link metaTokenInfoSchema} in particular,
 * because `GET /debug_token` answers identically for both and a second
 * near-identical token shape is a second place for the `expires_at: 0` bug to
 * be re-solved differently.
 *
 * ## THE ONE THING THIS SLICE REFUSES TO GUESS: WHAT THE TOKEN MAY DO
 *
 * A Meta system user token carries a set of scopes, and an ad account
 * connection is **not** one capability. `ads_read` lets Wend show an account.
 * `ads_management` lets Wend create, edit, start, pause and stop things that
 * spend money. Both are ordinary states of a working connection, and App Review
 * grants them separately.
 *
 * A screen that draws "Connected" from the existence of a row therefore says
 * something it has not checked. This is the zero-versus-absent rule pointed at
 * a permission instead of a number: *connected* and *connected and allowed to
 * spend* are different facts and only one of them is in the row. So
 * {@link metaAdsGrant} derives what a connection can actually do **from the
 * scopes `debug_token` reported**, and every screen reads it from there.
 *
 * `introspected: false` is its own answer and is not "no permissions". It means
 * Meta would not tell us, which is a reason to refuse a write rather than a
 * reason to claim one is forbidden — see {@link metaAdsGrant}.
 *
 * ## THE AD ACCOUNT ID IS STORED NUMERIC, AND PREFIXED AT THE CALL SITE
 *
 * Meta's Graph API addresses an ad account as `act_<number>`, while every other
 * object in this codebase is a bare number. `isMetaObjectId` — the guard that
 * makes path construction not an injection — refuses anything that is not
 * `^\d{1,24}$`, and `act_17843…` is not.
 *
 * The wrong repair is widening that regex, because it is the single check
 * standing between a database column and a fabricated Graph path. So the
 * numeric id is what is stored and validated, and the `act_` prefix is added by
 * code that owns the literal, immediately after the guard has run on the part
 * that came out of the database. The guard keeps its exact meaning and the
 * prefix is never user input.
 *
 * ## NOTHING HERE SPENDS OR READS SPEND
 *
 * Inventory, insights, budgets and spend are slice 41; writes are 42; start,
 * pause and stop are 43. This file describes a connection and what it is
 * permitted to do, so that the slices above it cannot be built on an assumed
 * permission. One figure of money does appear — {@link metaAdAccountSchema}'s
 * `currency` — and it is here rather than there on purpose: `surfaceFigures` in
 * `surface.ts` **throws** when spend arrives without a currency, so the
 * currency has to be a fact about the connection before the first spend figure
 * can exist at all.
 */
import { z } from 'zod';

import { isoDateTimeSchema, uuidSchema } from '../common.js';
import { integrationStatusSchema } from '../enums.js';
import { metaTokenInfoSchema } from './meta-token.js';

/* -------------------------------------------------------------------------- */
/* Ids                                                                         */
/* -------------------------------------------------------------------------- */

/**
 * A numeric Graph object id, matched exactly as the API's own `isMetaObjectId`
 * matches it.
 *
 * The duplication between this package and `core/meta/meta.graph.ts` is
 * deliberate and is not drift: the API's copy guards *path construction* and
 * must keep working if this package is ever absent from that build, while this
 * one refuses a bad id at the form, before it is ever stored. Two guards on the
 * same rule at two boundaries is the arrangement; one of them being deleted as
 * redundant is the failure.
 */
const metaObjectId = z
  .string()
  .regex(/^\d{1,24}$/, 'Meta ids are numeric. Paste the id, not the name or the URL.');

/** Business Manager id. Not secret. */
export const metaBusinessIdSchema = metaObjectId;

/**
 * Ad account id, **numeric and without the `act_` prefix**.
 *
 * A person copying from Ads Manager will have `act_` in their clipboard about
 * half the time, so the form strips it before validating rather than refusing
 * what Meta itself displays. See {@link normaliseAdAccountId}; the stored value
 * is always bare.
 */
export const metaAdAccountIdSchema = metaObjectId;

/**
 * Accepts what somebody actually pastes and returns what is stored.
 *
 * `act_17843` -> `17843`. Anything else is returned unchanged for the schema to
 * refuse with its own message, because a normaliser that also validates gives
 * two different error messages for one mistake.
 */
export function normaliseAdAccountId(pasted: string): string {
  const trimmed = pasted.trim();
  return trimmed.startsWith('act_') ? trimmed.slice(4) : trimmed;
}

/**
 * The Graph path segment for an ad account.
 *
 * The only place `act_` is ever written. It takes an id that has already been
 * through {@link metaAdAccountIdSchema} or the API's `isMetaObjectId`, so the
 * variable half is numeric and the prefix is a literal this function owns —
 * which is how the account can be addressed without widening the guard that
 * makes path construction safe.
 */
export function metaAdAccountPath(numericId: string): string {
  return `act_${numericId}`;
}

/* -------------------------------------------------------------------------- */
/* Scopes, and what they permit                                                */
/* -------------------------------------------------------------------------- */

/**
 * The two permissions this product cares about, spelled as Meta spells them in
 * `debug_token`'s `scopes` array.
 *
 * `business_management` is deliberately **not** required. It is needed to list
 * a business's ad accounts for a picker, and a connection where the person
 * typed the two ids themselves works without it. Requiring it would refuse a
 * working connection for the convenience of a form.
 */
export const META_ADS_SCOPE_READ = 'ads_read';
export const META_ADS_SCOPE_MANAGE = 'ads_management';

/**
 * What a connection may actually do, derived from the token — never from the
 * existence of the row.
 *
 * Three states rather than two, and the third is the point:
 *
 *   - `canRead` / `canManage` — Meta said the token carries the scope.
 *   - `known: false` — `debug_token` did not answer, so **neither flag means
 *     anything** and both are false. A screen must say "not checked" here and
 *     not "no permission": the first invites a Test, the second invites the
 *     reader to go and change something in Business Manager that was never
 *     wrong.
 *
 * Every write path in slices 42 and 43 asks `canManage` and refuses on
 * `known: false`, because "we could not confirm this token may spend money" is
 * a refusal, not a permission.
 */
export const metaAdsGrantSchema = z
  .object({
    known: z.boolean(),
    canRead: z.boolean(),
    canManage: z.boolean(),
  })
  .strict();
export type MetaAdsGrant = z.infer<typeof metaAdsGrantSchema>;

/** The grant a connection has, read off the introspected token. */
export function metaAdsGrant(token: {
  readonly introspected: boolean;
  readonly valid: boolean;
  readonly scopes: readonly string[];
}): MetaAdsGrant {
  /*
   * An invalid token grants nothing and the fact is KNOWN — Meta answered and
   * said so. That is different from `introspected: false`, where Meta did not
   * answer at all, and the two must not collapse into one grey state: one says
   * "reconnect", the other says "try again".
   */
  if (!token.introspected) return { known: false, canRead: false, canManage: false };
  if (!token.valid) return { known: true, canRead: false, canManage: false };

  const canManage = token.scopes.includes(META_ADS_SCOPE_MANAGE);
  return {
    known: true,
    /*
     * `ads_management` implies read. Meta grants the two separately and a token
     * carrying only management is unusual, but a connection that can create a
     * campaign and is drawn as unable to read one would be a screen arguing
     * with itself.
     */
    canRead: canManage || token.scopes.includes(META_ADS_SCOPE_READ),
    canManage,
  };
}

/* -------------------------------------------------------------------------- */
/* What a connection keeps in the clear                                        */
/* -------------------------------------------------------------------------- */

/**
 * `integrations.config` for a `meta_ads` row. Two ids, exactly as the WhatsApp
 * arm keeps two ids, and for the same reason: everything else a person
 * recognises the account by — its name, its currency, its status, what the
 * token may do — comes from Meta, changes without telling us, and is read off
 * `last_check` so that a stale copy cannot be drawn as current.
 *
 * `.strict()` because `integrations_config_has_no_secret` is a CHECK on
 * top-level keys and this is the layer that stops a fourth key arriving at all.
 */
export const metaAdsIntegrationConfigSchema = z
  .object({
    businessId: metaBusinessIdSchema,
    adAccountId: metaAdAccountIdSchema,
  })
  .strict();
export type MetaAdsIntegrationConfig = z.infer<typeof metaAdsIntegrationConfigSchema>;

/* -------------------------------------------------------------------------- */
/* What a check learned                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Meta's own account status codes, as the names this product uses for them.
 *
 * The numbers are Meta's (`1` active, `2` disabled, `3` unsettled, `7` pending
 * review, `9` in grace period, `101` closed). They are mapped to words here
 * rather than rendered, because a screen printing "Account status 3" has told
 * the reader nothing they can act on, and because an unrecognised code must
 * become `unknown` rather than a number nobody can look up.
 */
export const META_AD_ACCOUNT_STATUS_VALUES = [
  'active',
  'disabled',
  'unsettled',
  'pending_review',
  'grace_period',
  'closed',
  'unknown',
] as const;
export const metaAdAccountStatusSchema = z.enum(META_AD_ACCOUNT_STATUS_VALUES);
export type MetaAdAccountStatus = z.infer<typeof metaAdAccountStatusSchema>;

/** Meta's numeric `account_status`, mapped. Anything unlisted is `unknown`. */
export function metaAdAccountStatus(code: number | null | undefined): MetaAdAccountStatus {
  switch (code) {
    case 1:
      return 'active';
    case 2:
      return 'disabled';
    case 3:
      return 'unsettled';
    case 7:
      return 'pending_review';
    case 9:
      return 'grace_period';
    case 101:
      return 'closed';
    default:
      return 'unknown';
  }
}

/**
 * Whether an account in this state can have money spent through it.
 *
 * Used by slice 43's control paths, and stated here beside the status it reads
 * so the two cannot be given different answers in two files. `unknown` is false
 * for the same reason `known: false` grants nothing.
 */
export function metaAdAccountCanSpend(status: MetaAdAccountStatus): boolean {
  return status === 'active' || status === 'grace_period';
}

/**
 * The ad account as the last successful check saw it.
 *
 * `currency` is an ISO 4217 code and is **not nullable**, which is the one
 * shape decision in this file that reaches forward into every later slice:
 * `surfaceFigures` throws when a spend figure arrives without a currency, so an
 * account whose currency Meta did not report must fail its check rather than
 * connect and then produce unitless money on a screen.
 */
export const metaAdAccountSchema = z
  .object({
    id: metaAdAccountIdSchema,
    name: z.string(),
    currency: z.string().length(3),
    timezoneName: z.string(),
    status: metaAdAccountStatusSchema,
  })
  .strict();
export type MetaAdAccount = z.infer<typeof metaAdAccountSchema>;

/** The Business Manager the account belongs to. */
export const metaAdsBusinessSchema = z
  .object({ id: metaBusinessIdSchema, name: z.string() })
  .strict();
export type MetaAdsBusiness = z.infer<typeof metaAdsBusinessSchema>;

/**
 * What a **successful** `meta_ads` check learned. Stored verbatim in
 * `integrations.last_check`, drawn on Integrations.
 *
 * Nothing credential-shaped is in here and nothing may be added that is. The
 * `token` key is why migration 0028 extends 0013's tripwire to this kind: the
 * database refuses a top-level `token` for every kind but the two Meta ones,
 * and for those it checks the *inside* of the object instead. See that file.
 *
 * `transport` is on the snapshot because a green check taken through the stub
 * proves nothing about a real ad account, and a screen that cannot tell the two
 * apart is a screen that will eventually claim a fabricated account is live.
 */
export const metaAdsCheckResultSchema = z
  .object({
    transport: z.enum(['live', 'stub']),
    business: metaAdsBusinessSchema,
    adAccount: metaAdAccountSchema,
    token: metaTokenInfoSchema,
    checkedAt: isoDateTimeSchema,
  })
  .strict();
export type MetaAdsCheckResult = z.infer<typeof metaAdsCheckResultSchema>;

/**
 * The credential, sealed. **Never on a wire and never on a screen.**
 *
 * It lives in this file rather than in the API for the reason the WhatsApp
 * equivalent does not: it is a *type*, it is imported by the API alone, and
 * putting it beside the config it is stored next to keeps one file describing
 * one integration. It is exported as a type only — there is no schema and no
 * parser here, because nothing should ever be deserialising one of these from
 * anywhere except the envelope it was sealed in.
 */
export interface MetaAdsCredentials {
  /** Secret. Sent as `Authorization: Bearer …`, never as a query parameter. */
  readonly systemUserToken: string;
  /** Numeric Business Manager id. Not secret. */
  readonly businessId: string;
  /** Numeric ad account id, without `act_`. Not secret. */
  readonly adAccountId: string;
}

/* -------------------------------------------------------------------------- */
/* The redacted read shape                                                     */
/* -------------------------------------------------------------------------- */

/**
 * A `meta_ads` row as the API is allowed to describe it.
 *
 * It obeys `integration.ts`'s rule unchanged: **no field a credential could
 * travel in**, and `.strict()`, so returning one would fail this server's parse
 * of its own response rather than depend on somebody noticing a stray property
 * in a mapper. There is no `tokenLast4` here for the same reason the WhatsApp
 * arm has none — no fragment of a bearer token is safe to return.
 *
 * ## WHY `grant` IS ON THE WIRE AND `canSpend` IS NOT
 *
 * Both are derivable from fields already here, so neither is data the browser
 * lacks. The difference is who else asks the question.
 *
 * `grant` is asked by the API's own write guards in slices 42 and 43 — "may
 * this token pause a campaign that is spending money" is answered server-side
 * before anything is sent to Meta, whatever a screen thinks. It therefore
 * exists on the server regardless, and putting it on the wire means the screen
 * and the guard read one answer. That is the same argument `sesReadiness` makes
 * for deriving a verdict once instead of twice.
 *
 * `canSpend` is a one-line switch over {@link metaAdAccountStatus}, which is on
 * this shape, and nothing server-side branches on it. A second derived boolean
 * on a read shape is a field somebody has to keep in step for no gain, so it
 * stays a pure function both sides call.
 */
export const metaAdsIntegrationSchema = z
  .object({
    id: uuidSchema,
    kind: z.literal('meta_ads'),
    status: integrationStatusSchema,
    /** The Business Manager connected. Not secret. */
    businessId: z.string(),
    /** The ad account connected, numeric and without `act_`. Not secret. */
    adAccountId: z.string(),
    /*
     * Meta's own values, read off the last successful check rather than copied
     * into `config` at connect time — they change at Meta without telling us,
     * and a copy beside the ids would be stale from the moment the account
     * changed until the next Test.
     */
    /** The account's name at Meta, as of the last successful check. */
    accountName: z.string().nullable(),
    /** ISO 4217, as of the last successful check. Null until one has run. */
    currency: z.string().nullable(),
    /** The account's reporting timezone. Spend and insights are reported in it. */
    timezoneName: z.string().nullable(),
    /** Null until a check has succeeded — not `unknown`, which is an answer. */
    accountStatus: metaAdAccountStatusSchema.nullable(),
    /**
     * What the token may do, derived from the scopes the last check reported.
     * `{ known: false }` until a check has succeeded, which is the same state
     * an unintrospectable token produces and means the same thing: not checked.
     */
    grant: metaAdsGrantSchema,
    /**
     * Null means **either** "never expires" **or** "Meta would not say", and a
     * screen must not draw the second as the first.
     * `lastCheck.token.introspected` is what tells them apart — the identical
     * trap the WhatsApp arm carries, and the reason both read it off the same
     * `metaTokenInfoSchema`.
     */
    tokenExpiresAt: isoDateTimeSchema.nullable(),
    /** When the last **successful** check ran. Null until one has. */
    lastVerifiedAt: isoDateTimeSchema.nullable(),
    /** Why the last check failed, in repair terms. Never a credential. */
    lastError: z.string().nullable(),
    /** Snapshot from the last successful check. Null until one has succeeded. */
    lastCheck: metaAdsCheckResultSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
  })
  .strict();
export type MetaAdsIntegration = z.infer<typeof metaAdsIntegrationSchema>;

/**
 * The grant a connection with no successful check has: nothing, and knowingly
 * nothing-is-not-known.
 *
 * Exported so the view mapper and the suites use one literal rather than each
 * writing `{ known: false, canRead: false, canManage: false }` and one of them
 * later writing `known: true` by accident.
 */
export const META_ADS_GRANT_UNKNOWN: MetaAdsGrant = {
  known: false,
  canRead: false,
  canManage: false,
};

/* -------------------------------------------------------------------------- */
/* What a check answers with                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Which transport answered an ads check.
 *
 * Carried all the way to the screen, as it is for SES and WhatsApp, and for the
 * same reason: a green check taken through the stub proves nothing about a real
 * ad account, and a result that cannot say where it came from is how a
 * fabricated account gets mistaken for a live one. It matters more here than
 * anywhere else in the product, because the next slices spend money through
 * whatever this said was connected.
 */
export const META_ADS_TRANSPORT_VALUES = ['live', 'stub'] as const;
export const metaAdsTransportSchema = z.enum(META_ADS_TRANSPORT_VALUES);
export type MetaAdsTransport = z.infer<typeof metaAdsTransportSchema>;

/**
 * Why a check failed, in terms of **what the person has to fix** — not in
 * Meta's error codes, which are numbers with several meanings each.
 *
 * Three of these are deliberately not collapsed into one "access denied",
 * because they send somebody to three different places:
 *
 *   - `missing_ads_permission` — the token authenticated and does not carry
 *     `ads_read`/`ads_management`. The repair is App Review or regranting the
 *     system user, and it is the single most likely failure while this product
 *     is new to an account.
 *   - `access_denied` — the token is fine and this ad account is not visible to
 *     it. The repair is assigning the account to the system user in Business
 *     Manager, or a mistyped id.
 *   - `ad_account_disabled` — everything authenticated and Meta will not spend.
 *     The repair is billing, and no amount of reconnecting will change it.
 *
 * "Check your credentials" is none of those three, which is why the mapping
 * from Meta's numeric codes lives in one table in the API's `core/meta-ads`.
 */
export const META_ADS_FAILURE_CODE_VALUES = [
  'invalid_token',
  'token_expired',
  'missing_ads_permission',
  'access_denied',
  'unknown_object',
  'ad_account_disabled',
  'rate_limited',
  'network',
  'unknown',
] as const;
export const metaAdsFailureCodeSchema = z.enum(META_ADS_FAILURE_CODE_VALUES);
export type MetaAdsFailureCode = z.infer<typeof metaAdsFailureCodeSchema>;

/**
 * A failed check.
 *
 * SECURITY: `message` is persisted to `integrations.last_error` and shown on a
 * screen. It carries the repair, never the credential — no token, no fragment
 * of one, and no raw Graph error body, which can echo the request back.
 */
export const metaAdsCheckFailureSchema = z
  .object({
    code: metaAdsFailureCodeSchema,
    /** What to fix, in a sentence. Never contains the token. */
    message: z.string(),
    checkedAt: isoDateTimeSchema,
    transport: metaAdsTransportSchema,
  })
  .strict();
export type MetaAdsCheckFailure = z.infer<typeof metaAdsCheckFailureSchema>;

/**
 * What the Test action answers with.
 *
 * A union rather than an HTTP status distinction, for the reason the SES and
 * WhatsApp outcomes give: a rejected credential is a *result* of the test,
 * recorded on the row and rendered in the same card as a success, not a 4xx the
 * browser has to reverse-engineer.
 */
export const metaAdsCheckOutcomeSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), result: metaAdsCheckResultSchema }).strict(),
  z.object({ ok: z.literal(false), failure: metaAdsCheckFailureSchema }).strict(),
]);
export type MetaAdsCheckOutcome = z.infer<typeof metaAdsCheckOutcomeSchema>;

/* -------------------------------------------------------------------------- */
/* Connect                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Connect (or replace) a tenant's ad account.
 *
 * There is **no partial update**, for the reason `connectSesRequestSchema`
 * gives: `integrations` is unique on `(tenant_id, kind)`, connecting again
 * replaces what is there, and a half-updated credential — new account, old
 * token — is a state worth not having because it fails when somebody presses
 * pause rather than here at the form.
 *
 * SECURITY: `systemUserToken` must never survive the request. It goes to the
 * envelope cipher and nowhere else: not to a log line, not to an audit row, not
 * into `integrations.config`, and not back out in any response. Nothing in this
 * file provides a route for it to.
 */
export const connectMetaAdsRequestSchema = z
  .object({
    /** Secret. See above. */
    systemUserToken: z.string().trim().min(20).max(1024),
    businessId: metaBusinessIdSchema,
    /*
     * Not `metaAdAccountIdSchema` directly: Ads Manager shows `act_17843…` and
     * that is what half of all pastes will contain, so the prefix is stripped
     * before the numeric rule is applied rather than being refused as a typo
     * the person did not make.
     */
    adAccountId: z.preprocess(
      (value) => (typeof value === 'string' ? normaliseAdAccountId(value) : value),
      metaAdAccountIdSchema,
    ),
  })
  .strict();
export type ConnectMetaAdsRequest = z.infer<typeof connectMetaAdsRequestSchema>;
